<script>
  import { getContext } from 'svelte';
  import { format } from 'svelte-i18n';
  import { link, navigate, useParams } from 'svelte-navigator';
  import { Breadcrumb, BreadcrumbItem, Button, Col, Container, Row } from '@sveltestrap/sveltestrap';
  import { createQuery } from '@tanstack/svelte-query';

  import { Loader } from '@client/components/loader';
  import { getNotificationsContext } from '@client/components/notifications';

  import { AssetsService } from '@client/services/assets';

  import { ASSET_TYPES } from '@client/enums';

  import { deepCopy } from '@common/utils/objects';

  import { EditDocument } from './edit-document';
  import { EditImage } from './edit-image';
  import { EditVideo } from './edit-video';

  const { addNotification } = getNotificationsContext();
  const { basepath } = getContext('config');

  const params = useParams();

  let loading = false;
  let mimeType = '';
  let invalidFilename = false;
  let invalidRulename = false;
  let isDisabled = false;
  let originalData = {};
  let type = $params.type;
  let data = {};

  const notify = (text, type = 'success', autohide = 2500, position = 'top-right') => {
    addNotification({ text, type, autohide, position });
  };

  const query = createQuery({
    refetchOnWindowFocus: false,
    queryKey: ['edit-assets', $params.id, $params.type],
    queryFn: async () => {
      const response = await AssetsService.getByHash($params.id, $params.type);

      if (!response) {
        navigate(`${basepath}/manage/assets`);
        return;
      }

      originalData = deepCopy({
        ...response,
        noncompliant: response.noncompliant || false,
        viewonly: response.viewonly || false
      });

      data = deepCopy(originalData);

      return originalData;
    }
  });

  const onUpdateAsset = async () => {
    let response = {};

    loading = true;

    if (type === ASSET_TYPES.documents.id) {
      response = await AssetsService.updateDocument(data);
    } else if (type === ASSET_TYPES.images.id) {
      response = await AssetsService.updateImage(data);
    } else if (type === ASSET_TYPES.videos.id) {
      response = await AssetsService.updateVideo(data);
    }

    loading = false;

    if (response?.status >= 400) {
      notify($format('label.FAILURE.UPDATE', { values: { rulename: data.rulename } }), 'error');

      return;
    }

    originalData = deepCopy(data);

    notify($format('label.SUCCESS.UPDATE', { values: { item: data.rulename } }));
  };

  $: isDisabled =
    JSON.stringify(data) === JSON.stringify(originalData) ||
    (!data?.rulename?.length && type !== ASSET_TYPES.images.id) ||
    (type === ASSET_TYPES.images.id && !data?.filename?.includes(mimeType)) ||
    invalidRulename ||
    invalidFilename;
</script>

<div class="page">
  <Loader backdrop show={$query.isLoading || $query.isFetching || loading} />
  <div class="page-header">
    <Container fluid>
      <Row>
        <Col md="12">
          <Breadcrumb>
            <BreadcrumbItem>
              <a href="{basepath}/manage" use:link>{$format('label.MANAGE')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/manage/assets" use:link>{$format('label.ASSETS')}</a>
            </BreadcrumbItem>
            <BreadcrumbItem>
              <a href="{basepath}/manage/assets/{type}" use:link>{$format(ASSET_TYPES[type].label)}</a>
            </BreadcrumbItem>
            <BreadcrumbItem active>{$format('label.EDIT')}</BreadcrumbItem>
          </Breadcrumb>
        </Col>
      </Row>
      <Row class="flex-align--center">
        <Col xs="8" sm="6" md="6">
          <h1 class="page-title">
            {#if type === ASSET_TYPES.documents.id}
              {data?.rulename || ''}
            {:else if type === ASSET_TYPES.images.id}
              {data?.filename || ''}
            {:else if type === ASSET_TYPES.videos.id}
              {data?.rulename || ''}
            {/if}
          </h1>
        </Col>
        <Col xs="4" sm="6" md="6" class="text-right">
          <Button
            color="secondary"
            class="hide--mobile me-2"
            on:click={() => navigate(`${basepath}/manage/assets/${type}`)}
          >
            {$format('label.CANCEL')}
          </Button>

          <Button color="primary" on:click={onUpdateAsset} disabled={isDisabled}>{$format('label.UPDATE')}</Button>
        </Col>
      </Row>
    </Container>
  </div>
  <div class="page-content">
    <div class="page-width">
      {#if $query.data && !$query.isLoading && !$query.isFetching}
        {#if type === ASSET_TYPES.documents.id}
          <EditDocument bind:data bind:loading bind:invalidRulename />
        {:else if type === ASSET_TYPES.images.id}
          <EditImage bind:data bind:mimeType bind:loading bind:invalidFilename />
        {:else if type === ASSET_TYPES.videos.id}
          <EditVideo bind:data bind:loading bind:invalidRulename />
        {/if}
      {/if}
    </div>
  </div>
</div>

<style lang="scss" src="./edit.scss"></style>
